import {
	json,
	redirect,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { useLoaderData, useLocation } from '@remix-run/react'
import { useStoryblokState, StoryblokComponent } from '@storyblok/react'
import { useEffect, useRef } from 'react'
import { promiseHash } from 'remix-utils/promise'
import { type UTMParams } from '#app/constants/utm-types'
import { useUtmParams } from '#app/hooks/useUtmParams'
import { commitSession, getSession } from '#app/server/tracking/tracking'
import { isPreview } from '#app/utils/isPreview'
import { languageSession } from '#app/utils/localisation/lang.server'
import {
	getLanguageFromCookieOrBrowserOrFallback,
	isAcceptedLanguage,
} from '#app/utils/localisation/lokalisation-utilities'
import { combineHeaders } from '#app/utils/misc'
import {
	fetchSingleStory,
	fetchStories,
} from '#app/utils/server/storyblok-server-fetch'
import { trackPageView } from '#app/utils/tracking/amplitude/page-view'

type LoaderData = {
	storyData: any
	pageSource: string
	utmParams: UTMParams
}

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
	// avoid loading the www
	const url = new URL(request.url)
	if (url.hostname === 'www.plentyand.dk') {
		url.hostname = 'plentyand.dk'
		return redirect(url.toString(), { status: 301 })
	}
	const currentUrl = new URL(request.url).pathname

	const session = await getSession(request.headers.get('Cookie'))
	const previousUrl = session.get('currentUrl') || 'No previous URL'
	// Capture UTM parameters
	const referrer = request.headers.get('referer') || ''
	let utmParams: UTMParams = {}

	if (referrer) {
		try {
			const refUrl = new URL(referrer)
			utmParams = {
				utm_source: refUrl.searchParams.get('utm_source') || '',
				utm_medium: refUrl.searchParams.get('utm_medium') || '',
				utm_campaign: refUrl.searchParams.get('utm_campaign') || '',
				utm_term: refUrl.searchParams.get('utm_term') || '',
				utm_content: refUrl.searchParams.get('utm_content') || '',
			}
		} catch (error) {
			console.error('Error parsing referrer URL:', error)
		}
	}

	session.set('currentUrl', currentUrl)
	const pageSource = session.get('currentUrl') || 'No previous URL'
	session.set('pageSource', pageSource)

	let slug = params['*'] ?? 'home'
	const langSession = await languageSession.getSession(
		request.headers.get('cookie'),
	)
	let language: string = langSession.get('sessionLanguage') || 'da' // Default to Danish
	language = getLanguageFromCookieOrBrowserOrFallback(language, request)
	const locale = language
	const version = isPreview() ? 'draft' : 'published'

	const PER_PAGE_MAX = 100

	let sbParams: {
		version: 'draft' | 'published'
		language: string
	} = {
		version: version as 'draft' | 'published',
		language: isAcceptedLanguage(language) ? locale : 'da',
	}

	const { data, allStores, storesInformation } = await promiseHash({
		data: fetchSingleStory(slug, sbParams),
		allStores: fetchStories({
			...sbParams,
			starts_with: 'store-opening-times',
		}),
		storesInformation: fetchStories({
			...sbParams,
			per_page: PER_PAGE_MAX,
			starts_with: `stores-information`,
		}),
	})

	if (!data && params['*'] !== 'undefined') {
		return redirect('404-not-found')
	} else if (params['*'] === 'undefined') {
		return redirect('/')
	}

	// Check if this is a store page and redirect accordingly
	if (slug.startsWith('stores/')) {
		const storeSlug = slug.replace('stores/', '')
		const foundStore = storesInformation.data.stories.find(
			(story: { slug: string }) => story.slug === storeSlug,
		)

		if (
			foundStore &&
			foundStore.content.gender_store &&
			foundStore.content.gender_store.length > 0
		) {
			const genders = foundStore.content.gender_store
			const firstGender = genders[0]

			// Build the redirect URL with the exact format specified
			let redirectUrl = `/products/${storeSlug}/${firstGender}?store=${storeSlug}`

			// Only add chooseGender parameter if there are 2 or more genders
			if (genders.length >= 2) {
				redirectUrl += `&chooseGender=${genders.join(',')}`
			}

			return redirect(redirectUrl, { status: 301 })
		}
	}

	let storeOpeningTime = null
	const foundStore = storesInformation.data.stories.find(
		(story: { slug: string }) => `stores/${story.slug}` == slug,
	)
	if (foundStore?.content?.store_opening_hours?.[0]) {
		storeOpeningTime = foundStore.content.store_opening_hours[0].openingHours
	}

	return json(
		{
			storyData: data.data.story,
			openingTime: storeOpeningTime ? storeOpeningTime : null,
			allOpeningTimes: allStores.data.stories,
			locale,
			pageSource: previousUrl,
			storesInformation: storesInformation.data.stories,
			utmParams, // Add UTM params to the loader data
			currentUrl,
		},
		{
			headers: combineHeaders({
				'Set-Cookie': await commitSession(session),
				'Cache-Control': 'public, max-age=300', // Cache for 5 minutes
			}),
		},
	)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	const seoMetatags = data?.storyData?.content?.metatags
	const canonicalURL = data?.currentUrl
		.split('?')[0]
		.replace('https://www.plentyand.dk', 'https://plentyand.dk')

	if (seoMetatags) {
		return [
			seoMetatags.title && {
				title: seoMetatags.title,
			},
			{ tagName: 'link', rel: 'canonical', href: canonicalURL },
			seoMetatags.description && {
				name: 'description',
				content: seoMetatags.description,
			},
			//og
			seoMetatags.og_title && {
				property: 'og:title',
				content: seoMetatags.og_title,
			},
			seoMetatags.og_description && {
				property: 'og:description',
				content: seoMetatags.og_description,
			},
			seoMetatags.og_image && {
				property: 'og:image',
				content: seoMetatags.og_image,
			},
			//twitter
			seoMetatags.twitter_title && {
				property: 'twitter:title',
				content: seoMetatags.twitter_title,
			},
			seoMetatags.twitter_description && {
				property: 'twitter:description',
				content: seoMetatags.twitter_description,
			},
			seoMetatags.twitter_image && {
				property: 'twitter:image',
				content: seoMetatags.twitter_image,
			},
		].filter(Boolean)
	} else {
		return [
			{ title: 'Plenty &' },
			{
				name: 'description',
				content: 'Your own experience shopping in the city',
			},
		]
	}
}

export default function Page() {
	let { storyData, pageSource } = useLoaderData<LoaderData>()
	const utmParams = useUtmParams()
	const location = useLocation()
	const hasTrackedRef = useRef<string | null>(null)

	storyData = useStoryblokState(storyData)

	useEffect(() => {
		if (hasTrackedRef.current !== location.pathname) {
			trackPageView(location.pathname, pageSource, utmParams)
			hasTrackedRef.current = location.pathname
		}
	}, [location.pathname, pageSource, utmParams])

	return (
		<>
			<StoryblokComponent blok={storyData.content} />
		</>
	)
}
